import { useState, useRef } from 'react'
import { useBlogStore } from '../stores'
import { Pencil, Trash2, Image as ImageIcon, Upload, Loader2 } from 'lucide-react'
import { uploadToCloudinary } from '../utils/cloudinary'

const extractFirstImageSrc = (html: string | undefined) => {
  if (!html) return ''
  try {
    const doc = new DOMParser().parseFromString(html, 'text/html')
    const img = doc.querySelector('img')
    return img?.getAttribute('src') || ''
  } catch (error) {
    return ''
  }
}

function AdminBlogPosts() {
  const posts = useBlogStore((state) => state.posts)
  const createBlogPost = useBlogStore((state) => state.createBlogPost)
  const updateBlogPost = useBlogStore((state) => state.updateBlogPost)
  const deleteBlogPost = useBlogStore((state) => state.deleteBlogPost)

  const [formState, setFormState] = useState({
    title: '',
    summary: '',
    content: '<img src="" alt="Cover Image" />\n\n',
    tags: '',
    isPublished: true,
    cover_image_url: '',
  })
  const [editingId, setEditingId] = useState<string | null>(null)
  const [saveError, setSaveError] = useState('')
  const [uploadingCover, setUploadingCover] = useState(false)
  const coverFileRef = useRef<HTMLInputElement>(null)

  const handleCoverUpload = async (file: File) => {
    setUploadingCover(true)
    try {
      const res = await uploadToCloudinary(file, 'scoutang/blog')
      setFormState((prev) => ({ ...prev, cover_image_url: res.secure_url }))
    } catch (e: any) {
      alert(e?.message || 'Upload failed')
    } finally {
      setUploadingCover(false)
    }
  }

  const resetForm = () => {
    setEditingId(null)
    setFormState({ title: '', summary: '', content: '<img src="" alt="Cover Image" />\n\n', tags: '', isPublished: true, cover_image_url: '' })
  }

  const handleEdit = (id: string) => {
    const post = posts.find((post) => post.id === id)
    if (!post) return
    setEditingId(id)
    setFormState({
      title: post.title,
      summary: post.summary,
      content: post.content,
      tags: (post.tags || []).join(', '),
      isPublished: post.is_published ?? true,
      cover_image_url: post.cover_image_url || extractFirstImageSrc(post.content) || '',
    })
  }

  const handleSave = async () => {
    if (!formState.title.trim() || !formState.content.trim()) return
    setSaveError('')

    const resolvedCoverImage = formState.cover_image_url.trim() || extractFirstImageSrc(formState.content) || undefined

    const payload = {
      title: formState.title.trim(),
      summary: formState.summary.trim(),
      content: formState.content.trim(),
      tags: formState.tags.split(',').map((tag) => tag.trim()).filter(Boolean),
      is_published: formState.isPublished,
      published_at: new Date().toISOString(),
      author: 'Admin',
      cover_image_url: resolvedCoverImage,
    }

    try {
      if (editingId) {
        await updateBlogPost(editingId, payload)
      } else {
        await createBlogPost(payload)
      }
      resetForm()
    } catch (error) {
      setSaveError(error instanceof Error ? error.message : 'Failed to save blog post.')
    }
  }

  const handleDelete = async (id: string) => {
    setSaveError('')
    try {
      await deleteBlogPost(id)
    } catch (error) {
      setSaveError(error instanceof Error ? error.message : 'Failed to delete blog post.')
    }
  }

  return (
    <div className="space-y-8">
      <div>
        <p className="text-[10px] font-black uppercase tracking-[0.28em] text-emerald-400">Blog</p>
        <h2 className="mt-2 text-2xl font-black text-white">Blog Posts</h2>
        <p className="mt-1 text-sm text-slate-400">Manage public blog articles.</p>
      </div>

      <div className="grid gap-6 xl:grid-cols-[1.15fr_0.85fr]">
        <div className="space-y-6 rounded-[30px] border border-emerald-500/15 bg-gradient-to-br from-[#0d1117] via-[#0d141a] to-[#101b1c] p-6 shadow-[0_0_0_1px_rgba(20,184,166,0.08)]">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-[10px] font-black uppercase tracking-[0.28em] text-emerald-400">Blog editor</p>
              <h3 className="mt-2 text-2xl font-black text-white">Write a story</h3>
            </div>
            <div className="rounded-full border border-emerald-500/20 bg-emerald-500/10 px-3 py-1 text-[10px] font-black uppercase tracking-[0.25em] text-emerald-300">
              {editingId ? 'Update' : 'New'}
            </div>
          </div>

          <div className="grid gap-4">
            <label className="block text-sm font-semibold text-slate-200">
              Title
              <input
                value={formState.title}
                onChange={(e) => setFormState((prev) => ({ ...prev, title: e.target.value }))}
                className="mt-2 w-full rounded-2xl border border-slate-700 bg-slate-950/80 px-4 py-3 text-sm text-white outline-none transition focus:border-emerald-500"
                placeholder="Post title"
              />
            </label>

            <label className="block text-sm font-semibold text-slate-200">
              Summary
              <textarea
                value={formState.summary}
                onChange={(e) => setFormState((prev) => ({ ...prev, summary: e.target.value }))}
                className="mt-2 w-full rounded-2xl border border-slate-700 bg-slate-950/80 px-4 py-3 text-sm text-white outline-none transition focus:border-emerald-500"
                rows={3}
                placeholder="Short summary for this post"
              />
            </label>

            <label className="block text-sm font-semibold text-slate-200">
              Cover image
              <div className="mt-2 space-y-3">
                <input
                  value={formState.cover_image_url}
                  onChange={(e) => setFormState((prev) => ({ ...prev, cover_image_url: e.target.value }))}
                  className="w-full rounded-2xl border border-slate-700 bg-slate-950/80 px-4 py-3 text-sm text-white outline-none transition focus:border-emerald-500"
                  placeholder="https://.../cover-image.jpg or upload below"
                />
                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    onClick={() => coverFileRef.current?.click()}
                    disabled={uploadingCover}
                    className="inline-flex items-center gap-2 rounded-xl bg-emerald-600 px-4 py-2 text-xs font-bold text-white transition hover:bg-emerald-500 disabled:opacity-50"
                  >
                    {uploadingCover ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
                    {uploadingCover ? 'Uploading…' : 'Upload image'}
                  </button>
                  {formState.cover_image_url && (
                    <button
                      type="button"
                      onClick={() => setFormState((prev) => ({ ...prev, cover_image_url: '' }))}
                      className="text-xs font-semibold text-red-400 hover:text-red-300 transition"
                    >
                      Remove
                    </button>
                  )}
                </div>
                <input
                  ref={coverFileRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp,image/gif"
                  className="hidden"
                  onChange={(e) => {
                    const file = e.target.files?.[0]
                    if (file) handleCoverUpload(file)
                    e.target.value = ''
                  }}
                />
                {formState.cover_image_url && (
                  <div className="relative w-full max-w-md overflow-hidden rounded-2xl border border-slate-700">
                    <img
                      src={formState.cover_image_url}
                      alt="Cover preview"
                      className="w-full h-40 object-cover"
                      onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none' }}
                    />
                  </div>
                )}
              </div>
            </label>

            <label className="block text-sm font-semibold text-slate-200">
              <div className="mb-2 flex items-center justify-between gap-3">
                <span>Content</span>
                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={() => setFormState((prev) => ({ ...prev, content: prev.content + '\n<img src="" alt="Thumbnail" width="250" />\n' }))}
                    className="inline-flex items-center gap-1 rounded-lg bg-slate-800 px-2 py-1 text-[10px] font-bold uppercase tracking-[0.2em] text-slate-200 hover:bg-slate-700"
                  >
                    <ImageIcon size={12} /> Thumbnail
                  </button>
                  <button
                    type="button"
                    onClick={() => setFormState((prev) => ({ ...prev, content: prev.content + '\n<img src="" alt="Image" />\n' }))}
                    className="inline-flex items-center gap-1 rounded-lg bg-emerald-500/10 px-2 py-1 text-[10px] font-bold uppercase tracking-[0.2em] text-emerald-300 hover:bg-emerald-500/20"
                  >
                    <ImageIcon size={12} /> Media
                  </button>
                </div>
              </div>
              <textarea
                value={formState.content}
                onChange={(e) => setFormState((prev) => ({ ...prev, content: e.target.value }))}
                className="w-full rounded-2xl border border-slate-700 bg-slate-950/80 px-4 py-3 text-sm text-white outline-none transition focus:border-emerald-500 font-mono"
                rows={8}
                placeholder="Full article content"
              />
            </label>

            <div className="grid gap-4 md:grid-cols-2">
              <label className="block text-sm font-semibold text-slate-200">
                Tags
                <input
                  value={formState.tags}
                  onChange={(e) => setFormState((prev) => ({ ...prev, tags: e.target.value }))}
                  className="mt-2 w-full rounded-2xl border border-slate-700 bg-slate-950/80 px-4 py-3 text-sm text-white outline-none transition focus:border-emerald-500"
                  placeholder="news, fixtures, scouting"
                />
              </label>
              <label className="block text-sm font-semibold text-slate-200">
                Visibility
                <div className="mt-2 flex items-center gap-2 rounded-2xl border border-slate-700 bg-slate-950/80 px-4 py-3">
                  <input
                    type="checkbox"
                    checked={formState.isPublished}
                    onChange={(e) => setFormState((prev) => ({ ...prev, isPublished: e.target.checked }))}
                    className="h-4 w-4 rounded border-gray-700 bg-gray-900 text-emerald-500 focus:ring-emerald-500"
                  />
                  <span className="text-sm text-slate-300">Publish to public blog</span>
                </div>
              </label>
            </div>

            <div className="flex flex-wrap gap-3">
              <button
                type="button"
                onClick={() => void handleSave()}
                className="rounded-xl bg-emerald-600 px-5 py-3 text-sm font-semibold text-white transition hover:bg-emerald-500"
              >
                {editingId ? 'Update Post' : 'Create Post'}
              </button>
              {editingId && (
                <button
                  type="button"
                  onClick={resetForm}
                  className="rounded-xl bg-slate-800 px-5 py-3 text-sm font-semibold text-white transition hover:bg-slate-700"
                >
                  Cancel
                </button>
              )}
            </div>
            {saveError && <p role="alert" className="text-sm text-red-400">{saveError}</p>}
          </div>
        </div>

        <div className="rounded-[30px] border border-slate-800 bg-[#0d1117] p-6">
          <h3 className="text-xl font-black text-white">Published articles</h3>
          <div className="mt-5 space-y-3">
            {posts.length === 0 ? (
              <p className="text-sm text-slate-400">No posts yet. Create a new article above.</p>
            ) : (
              posts.map((post) => (
                <div key={post.id} className="rounded-3xl border border-slate-800 bg-slate-950/60 p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="text-[10px] font-black uppercase tracking-[0.22em] text-emerald-400">
                        {post.is_published ? 'Published' : 'Draft'}
                      </p>
                      <h4 className="mt-2 text-lg font-bold text-white">{post.title}</h4>
                      <p className="mt-1 text-xs text-slate-400">{new Date(post.published_at).toLocaleDateString()}</p>
                    </div>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() => handleEdit(post.id)}
                        className="rounded-xl border border-slate-700 bg-slate-900 p-2 text-slate-200 hover:border-emerald-500 hover:text-white"
                      >
                        <Pencil size={16} />
                      </button>
                      <button
                        type="button"
                        onClick={() => void handleDelete(post.id)}
                        className="rounded-xl border border-slate-700 bg-slate-900 p-2 text-red-400 hover:border-red-500 hover:text-white"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>
                  <p className="mt-3 text-sm text-slate-400 line-clamp-3">{post.summary}</p>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default AdminBlogPosts
